import React from "react";
import { Spinner } from "../../../components";
import { useAdminUsageOverview } from "../../../api/hooks/admin";
import {
  USAGE_CATEGORY_LABELS,
  UsageCategory,
} from "../../../api/admin/types";
import { formatInt } from "./formatters";

interface PlatformKpiStripProps {
  startDate: string;
  endDate: string;
  includeTestAccounts: boolean;
  showEpic: boolean;
}

// Pastel palette tuned to match the mockup. Saturated CATEGORY_COLORS would
// fight with the rest of the page; these stay as quiet backdrops behind
// the headline number.
const CARD_BG: Partial<Record<UsageCategory, string>> = {
  proposals: "#f3e8ff",          // lavender
  comparisons: "#dbeafe",        // blue
  gap_analyses: "#cffafe",       // cyan
  contract_reviews: "#fef3c7",   // cream
  single_policy_analyses: "#fce7f3", // pink
  sov: "#dcfce7",                // green
  loss_runs: "#ecfccb",          // mint
  coi: "#fed7aa",                // orange
  chats: "#ede9fe",              // soft purple
  epic_runs: "#e0f2fe",          // sky
};

// Order chosen to roughly match the mockup's left-to-right flow. epic_runs
// appears at the end so it reads as a separate cluster from human workflows;
// it's also conditionally hidden via the showEpic prop.
const SMALL_CARD_ORDER: UsageCategory[] = [
  "proposals",
  "comparisons",
  "gap_analyses",
  "contract_reviews",
  "single_policy_analyses",
  "sov",
  "loss_runs",
  "coi",
  "chats",
  "epic_runs",
];

const HEADLINE_BG = "#f8fafc"; // near-white with a hint of cool

const headlineCard: React.CSSProperties = {
  background: HEADLINE_BG,
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: "24px 28px",
  flex: "1 1 0",
  minWidth: 240,
  minHeight: 130,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const headlineNumber: React.CSSProperties = {
  fontSize: 56,
  fontWeight: 700,
  lineHeight: 1,
  color: "#1f2937",
};

const headlineLabel: React.CSSProperties = {
  fontSize: 15,
  color: "#6b7280",
  marginTop: 14,
  fontWeight: 500,
};

const smallCard = (bg: string): React.CSSProperties => ({
  background: bg,
  borderRadius: 10,
  padding: "16px 18px",
  flex: "1 1 0",
  minWidth: 130,
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const smallNumber: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  lineHeight: 1,
  color: "#1f2937",
};

const smallLabel: React.CSSProperties = {
  fontSize: 12,
  color: "#4b5563",
  fontWeight: 500,
  lineHeight: 1.3,
};

const PlatformKpiStrip: React.FC<PlatformKpiStripProps> = ({
  startDate,
  endDate,
  includeTestAccounts,
  showEpic,
}) => {
  const overview = useAdminUsageOverview(
    startDate && endDate
      ? { startDate, endDate, includeTestAccounts }
      : null
  );

  const cats = overview.data?.totals_by_category;
  const usersCount = overview.data?.active_user_count ?? 0;
  const policiesCount = overview.data?.upload_total ?? 0;
  // Filter epic_runs out of the small-card row when toggle is off so the
  // cluster stays as the existing 9 human categories.
  const visibleSmallCards = showEpic
    ? SMALL_CARD_ORDER
    : SMALL_CARD_ORDER.filter((c) => c !== "epic_runs");

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1200,
        margin: "0 auto",
        padding: "0 16px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {/* Headline row: two large cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={headlineCard}>
          <div style={headlineNumber}>
            {overview.isLoading ? <Spinner /> : formatInt(usersCount)}
          </div>
          <div style={headlineLabel}>Users</div>
        </div>
        <div style={headlineCard}>
          <div style={headlineNumber}>
            {overview.isLoading ? <Spinner /> : formatInt(policiesCount)}
          </div>
          <div style={headlineLabel}>Policies</div>
        </div>
      </div>

      {/* Per-category breakdown: pastel cards, one per workflow */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {visibleSmallCards.map((c) => {
          // Defensive: fall back to neutral if the BG map ever drifts.
          const bg = CARD_BG[c] ?? "#f3f4f6";
          const value = cats?.[c] ?? 0;
          return (
            <div key={c} style={smallCard(bg)}>
              <div style={smallNumber}>
                {overview.isLoading ? <Spinner /> : formatInt(value)}
              </div>
              <div style={smallLabel}>{USAGE_CATEGORY_LABELS[c]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformKpiStrip;
